import React, { useCallback, useEffect, useState } from "react"
// import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import { request } from "../../services/request"
import { FloorPlansData, FloorPlansDataResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { FaChevronLeft } from "react-icons/fa"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"

const Icon: any = FaChevronLeft

const FloorPlans: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()

    const [plans, setPlans] = useState<FloorPlansData[]>([])
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [totalItems, setTotalItems] = useState(100)
    const [search, setSearch] = useState("")
    const debounceText = useDebounce(search, 500)
    const [sort, setSort] = useState("DESC")
    const [sortCol, setSortCol] = useState("createdAt")
    const navigate = useNavigate()
    const param = useParams()
    const [selectedRows, setSelectedRows] = React.useState<FloorPlansData[]>([])

    const bulkActions = [
        {
            label: `Clear Alert${selectedRows.length > 1 ? "(s)" : ""}`,
            type: "button",
            onClick: () => {
                handleAlert()
            },
            className: "resetBtn",
        },
    ]

    const fetchFloorPlans = useCallback(async () => {
        try {
            dispatch(changeloaderState(true))

            const params = {
                page: page,
                limit: rowsPerPage,
                search: debounceText,
                sort: sort,
                sortCol: sortCol,
            }
            const response = await request<FloorPlansDataResponse>({
                url: `/adminCommon/getFloorPlans/${param.id}`,
                method: "POST",
                data: params,
            })

            if (response.success) {
                setPlans(response.data)
                setTotalItems(response.total)
            } else {
                toast.error("Failed to fetch Floor Plans: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        } finally {
            dispatch(changeloaderState(false))
        }
    }, [page, rowsPerPage, debounceText, sort, sortCol, dispatch, param.id])

    const handleAlert = async () => {
        const params = selectedRows.map((row: any) => row.id)
        try {
            const response = await request({
                url: `/adminCommon/clearFloorplanAlert`,
                method: "POST",
                headers: { apikey: process.env.REACT_APP_API_KEY || "gunsens-api" },
                data: params,
            })
            if (response.success) {
                toast.success(response.message)
                setSelectedRows([])
                fetchFloorPlans()
            } else {
                toast.error(response.message)
            }
        } catch (error: any) {
            toast.error(`${error.message}`)
        }
    }

    useEffect(() => {
        fetchFloorPlans()
    }, [page, rowsPerPage, debounceText, sort, fetchFloorPlans])

    useEffect(() => {
        if (debounceText) {
            setPage(1) // Reset to first page on search
        }
    }, [debounceText])

    const columns = [
        {
            name: "Plan Image",
            value: (plan: FloorPlansData) => plan,
            id: "planimage",
            type: "planimage",
            className: "table-image"
        },
        {
            name: "Name",
            value: (plan: FloorPlansData) => plan.name,
            id: "name",
        },
        {
            name: "Total Sensors",
            value: (plan: FloorPlansData) => plan.totalsensors,
            id: "totalsensors",
        },
        {
            name: "Activated Sensors",
            value: (plan: FloorPlansData) => plan.activatedsensors,
            id: "activatedsensors",
        },
        {
            name: "Deactivated Sensors",
            value: (plan: FloorPlansData) => plan.deactivatedsensors,
            id: "deactivatedsensors",
        },
    ]

    const getBack = () => {
        navigate(-1)
    }

    const action: TableAction[] = [
        {
            label: "Sensors",
            // onClick: (row: FloorPlansData) => {
            //     navigate(`/location/sensors/${row.id}`)
            // },
            isRedirect: true,
            link: (row: FloorPlansData) => `/location/sensors/${row.id}`,
            className: "table-btn-1",
        },
    ]

    const fetchAllPlans = async () => {
        try {
            const params = {
                page: page,
                search: debounceText,
                limit: 100000,
                sort: sort,
                sortCol: sortCol,
            }
            const response = await request<FloorPlansDataResponse>({
                url: `/adminCommon/getFloorPlans/${param.id}`,
                method: "POST",
                data: params,
            })

            if (response.success) {
                return response.data
            } else {
                toast.error("Failed to fetch Floor Plans: " + response.message)
                return []
            }
        } catch (error: any) {
            toast.error(`Error fetching all Floor Plans: ${error.message}`)
            return []
        }
    }

    const exportCSV = async () => {

        const allData = await fetchAllPlans()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = [
            "Sr. No.",
            "Name",
            "Total Sensors",
            "Activated Sensors",
            "Deactivated Sensors",
        ]

        // ✅ Correct rows mapping for Floor Plans data
        const rows = allData.map((plan, index) => [
            index + 1,
            plan.name,
            plan.totalsensors,
            plan.activatedsensors,
            plan.deactivatedsensors,
        ])

        donwloadCSV(headers, rows, "Floor-plans.csv")
    }

    const exportPDF = async () => {

        const allData = await fetchAllPlans()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = [
            "Sr. No.",
            "Name",
            "Total Sensors",
            "Activated Sensors",
            "Deactivated Sensors",
        ]

        // ✅ Correct rows mapping for Floor Plans data
        const rows = allData.map((plan, index) => [
            index + 1, // Sr. No.
            plan.name,
            plan.totalsensors,
            plan.activatedsensors,
            plan.deactivatedsensors,
        ])

        // call utility
        downloadPDF(headers, rows, "Floor-plans")
    }

    return (
        <section className=' dashboard-section dashboard-item '>
            <ToastContainer position='top-right' autoClose={2000} theme='light' />
            <div className='container'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='d-flex gap-1 mb-2'>
                            <button onClick={getBack} className='d-flex align-items-center'>
                                <Icon size={20} />
                            </button>
                            <div className='dashboard-table-title-section w-100'>
                                <h2>Floor Plans</h2>
                                <div className='dashboard-table-title-right-side d-flex gap-2'>
                                    <PrimaryButton
                                        label='Export CSV'
                                        className='max-w-max-content'
                                        onClick={exportCSV}
                                    />
                                    <PrimaryButton
                                        label='Export PDF'
                                        className='max-w-max-content'
                                        onClick={exportPDF}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='col-12 mt-4'>
                        <div className='table-container-main'>
                            <div className='table-details-section'></div>
                            <Table
                                rowData={plans}
                                columns={columns}
                                page={page}
                                setPage={setPage}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                totalItems={totalItems}
                                setSortColumn={setSortCol}
                                setSortOrder={setSort}
                                sortColumn={sortCol}
                                sortOrder={sort}
                                isSearch
                                search={search}
                                setSearch={setSearch}
                                action={action}
                                {...(selectedRows.length > 0 && { bulkActions })}
                                setSelected={setSelectedRows}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default FloorPlans
